import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import {
  getScheduledJobs,
  getScheduledJob,
  updateScheduledJob,
  updateScheduledJobsConfig,
  triggerJobRun,
  jobToRunResult,
  ScheduledJob,
  ScheduledJobConfig,
  JobUpdateBody,
  TriggerRunResponse,
  RunOptions,
} from '../../api/scheduledJobs';
import EditJobDialog from './EditJobDialog';
import RunOptionsDialog from './RunOptionsDialog';
import RunResultDialog from './RunResultDialog';
import BatchBackfillReviewDialog, {
  BatchBackfillReviewItem,
} from '../ContentCompletionPage/BatchBackfillReviewDialog';
import {
  BackfillFeedType,
} from '../../types/contentCompletion';
import {
  buildLatestNewsReviewItems,
  countFoundLatestNews,
} from './latestNewsUtils';
import { useNotification } from '../../context/NotificationContext';
import { useEnvironment } from '../../hooks/useEnvironment';
import type { Environment } from '../../types/environment';
import ErrorMessage from '../../components/common/ErrorMessage/ErrorMessage';
import styles from './ScheduledJobsPage.module.css';

const ScheduledJobsPage: React.FC = () => {
  const { showNotification } = useNotification();
  const { environment: uiEnvironment, setEnvironment } = useEnvironment();
  const [jobs, setJobs] = useState<ScheduledJob[]>([]);
  const [environment, setJobsEnvironment] = useState<string>('sandbox');
  const [schedulerTimezone, setSchedulerTimezone] = useState<string>(
    'Pacific (PST/PDT)'
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [runningJobId, setRunningJobId] = useState<string | null>(null);
  const [editJob, setEditJob] = useState<ScheduledJobConfig | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [runResult, setRunResult] = useState<TriggerRunResponse | null>(null);
  const [runResultJobName, setRunResultJobName] = useState('');
  const [runResultJobType, setRunResultJobType] = useState<string | undefined>();
  const [runResultDialogOpen, setRunResultDialogOpen] = useState(false);
  const [runOptionsJob, setRunOptionsJob] = useState<ScheduledJob | null>(null);
  const [runOptionsDialogOpen, setRunOptionsDialogOpen] = useState(false);
  const [runResultDialogRunning, setRunResultDialogRunning] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState<string | null>(null);
  const [batchReviewOpen, setBatchReviewOpen] = useState(false);
  const [batchReviewItems, setBatchReviewItems] = useState<BatchBackfillReviewItem[]>(
    []
  );
  const [batchDaysBack, setBatchDaysBack] = useState(2);
  const [batchFeedType, setBatchFeedType] = useState<BackfillFeedType>('auto');
  const abortControllerRef = useRef<AbortController | null>(null);
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getScheduledJobs();
      setJobs(data.jobs);
      setJobsEnvironment(data.environment || 'sandbox');
      setSchedulerTimezone(data.scheduler_timezone || 'Pacific (PST/PDT)');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (err as Error)?.message ||
        'Failed to load scheduled jobs';
      setError(msg);
      showNotification(msg, 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleEditClick = async (jobId: string) => {
    try {
      const config = await getScheduledJob(jobId);
      setEditJob(config);
      setEditDialogOpen(true);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (err as Error)?.message ||
        'Failed to load job config';
      showNotification(msg, 'error');
    }
  };

  const handleEditSave = async (
    jobId: string,
    data: JobUpdateBody,
    environmentUpdate?: string
  ): Promise<void> => {
    try {
      await updateScheduledJob(jobId, data);
      if (environmentUpdate !== undefined) {
        await updateScheduledJobsConfig({ environment: environmentUpdate });
      }
      showNotification('Job updated successfully', 'success');
      await fetchJobs();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ||
        (err as Error)?.message ||
        'Failed to update job';
      showNotification(msg, 'error');
      throw err;
    }
  };

  const handleRunNowClick = (jobId: string) => {
    const job = jobs.find((j) => j.id === jobId);
    if (job) {
      setRunOptionsJob(job);
      setRunOptionsDialogOpen(true);
    }
  };

  const openLatestNewsReview = useCallback(
    (summary: TriggerRunResponse['run_summary'], envUsed?: string) => {
      const built = buildLatestNewsReviewItems(summary);
      if (built.items.length === 0) {
        showNotification('No press releases ready to review', 'warning');
        return;
      }
      if (envUsed === 'sandbox' || envUsed === 'production') {
        if (uiEnvironment !== envUsed) {
          setEnvironment(envUsed as Environment);
        }
      }
      setBatchDaysBack(built.daysBack);
      setBatchFeedType(built.feedType);
      setBatchReviewItems(built.items);
      setBatchReviewOpen(true);
      showNotification(
        `Ready to review ${built.items.length} compan${
          built.items.length === 1 ? 'y' : 'ies'
        }`,
        'success'
      );
    },
    [showNotification, uiEnvironment, setEnvironment]
  );

  const handleRunWithOptions = async (options: RunOptions) => {
    if (!runOptionsJob) return;
    const jobId = runOptionsJob.id;
    const jobType = runOptionsJob.type;
    const jobDisplayName = runOptionsJob.display_name || jobId;
    const envUsed = options.environment || environment;
    setRunningJobId(jobId);
    setRunOptionsDialogOpen(false);
    setRunOptionsJob(null);
    setRunResult(null);
    setRunResultJobName(jobDisplayName);
    setRunResultJobType(jobType);
    setRunResultDialogOpen(true);
    setRunResultDialogRunning(true);
    abortControllerRef.current = new AbortController();
    try {
      await triggerJobRun(jobId, options, abortControllerRef.current.signal);
      setRunResult({
        job_id: jobId,
        status: 'running',
        last_run_at: null,
        last_run_error: null,
        environment_used: envUsed,
        run_summary: {
          companies_processed: 0,
          companies_with_errors: 0,
          companies_total: 0,
          companies_current: 0,
          log_lines: ['Starting job…'],
        },
      });
      const poll = async () => {
        if (pollIntervalRef.current === null) return;
        try {
          const data = await getScheduledJobs();
          const job = data.jobs.find((j) => j.id === jobId);
          if (job) {
            const result = jobToRunResult(job, data.environment || envUsed);
            // Prefer the environment the user selected for this run.
            result.environment_used = envUsed;
            setRunResult(result);
            if (job.last_run_status !== 'running') {
              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
              setRunResultDialogRunning(false);
              setRunningJobId(null);
              await fetchJobs();
              if (result.status === 'success' || result.status === 'partial') {
                showNotification('Job completed successfully', 'success');
                if (
                  jobType === 'latest_news_agent' &&
                  countFoundLatestNews(result.run_summary) > 0
                ) {
                  openLatestNewsReview(result.run_summary, envUsed);
                }
              } else if (result.status === 'failure') {
                showNotification(
                  result.last_run_error || 'Job failed',
                  'error'
                );
              }
            }
          }
        } catch {
          // ignore poll errors
        }
      };
      pollIntervalRef.current = setInterval(poll, 2000);
      poll();
    } catch (err: unknown) {
      const isAborted =
        (err as Error)?.name === 'CanceledError' ||
        (err as Error)?.name === 'AbortError';
      if (!isAborted) {
        const msg =
          (err as { response?: { data?: { detail?: string } } })?.response?.data
            ?.detail ||
          (err as Error)?.message ||
          'Failed to run job';
        showNotification(msg, 'error');
      }
      setRunResult({
        job_id: jobId,
        status: isAborted ? 'cancelled' : 'failure',
        last_run_at: null,
        last_run_error: isAborted
          ? 'Cancelled by user'
          : (err as Error)?.message,
        environment_used: envUsed,
        run_summary: {
          companies_processed: 0,
          companies_with_errors: 0,
          log_lines: isAborted ? ['Run cancelled by user'] : [],
        },
      });
      setRunResultDialogRunning(false);
      setRunningJobId(null);
      await fetchJobs();
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleCancelRun = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setRunResultDialogRunning(false);
    setRunningJobId(null);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const formatLastRun = (lastRunAt: string | null) => {
    if (!lastRunAt) return '—';
    try {
      const d = new Date(lastRunAt);
      return d.toLocaleString();
    } catch {
      return lastRunAt;
    }
  };

  const getStatusChip = (job: ScheduledJob) => {
    const status = job.last_run_status;
    if (status === 'success') {
      return <Chip label="Success" color="success" size="small" />;
    }
    if (status === 'failure') {
      return <Chip label="Failure" color="error" size="small" />;
    }
    return <Chip label={status || 'Never'} size="small" variant="outlined" />;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" className={styles.container}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className={styles.container}>
      <Typography variant="h4" component="h1" gutterBottom>
        Scheduled Jobs
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        View and manually trigger scheduled jobs. Schedule times are in{' '}
        <strong>{schedulerTimezone}</strong>. Click the arrow to expand and view
        last run details (refresh after a scheduled run to see results).
      </Typography>

      {error && (
        <ErrorMessage message={error} onDismiss={() => setError(null)} />
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ width: 40 }} />
              <TableCell>Job Name</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Environment</TableCell>
              <TableCell>Last Run</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No scheduled jobs configured
                </TableCell>
              </TableRow>
            ) : (
              jobs.map((job) => (
                <React.Fragment key={job.id}>
                  <TableRow>
                    <TableCell padding="checkbox" sx={{ width: 40 }}>
                      {job.run_summary?.log_lines?.length ? (
                        <Button
                          size="small"
                          onClick={() =>
                            setExpandedJobId(
                              expandedJobId === job.id ? null : job.id
                            )
                          }
                          sx={{ minWidth: 0, p: 0.5 }}
                        >
                          {expandedJobId === job.id ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </Button>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{job.display_name}</Typography>
                      {job.type === 'document_intelligence' && job.spend_mtd && (
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(`${job.spend_mtd.month}-01T00:00:00Z`).toLocaleString(
                              'en-US',
                              { month: 'short', year: 'numeric', timeZone: 'UTC' }
                            )}{' '}
                            MTD total: ${job.spend_mtd.total_cost_usd.toFixed(4)}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={0.5}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{ mt: 0.5 }}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Milestone $${job.spend_mtd.analyses.milestone.cost_usd.toFixed(4)}`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Corporate Transactions $${job.spend_mtd.analyses.corporate_transactions.cost_usd.toFixed(4)}`}
                            />
                            <Chip
                              size="small"
                              variant="outlined"
                              label={`Pipeline $${job.spend_mtd.analyses.pipeline.cost_usd.toFixed(4)}`}
                            />
                          </Stack>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {job.schedule}
                      {schedulerTimezone && ` (${schedulerTimezone})`}
                    </TableCell>
                    <TableCell>{environment || '—'}</TableCell>
                    <TableCell>{formatLastRun(job.last_run_at)}</TableCell>
                    <TableCell>{getStatusChip(job)}</TableCell>
                    <TableCell>
                      {job.email_enabled ? (
                        <Chip label="On" size="small" color="primary" />
                      ) : (
                        <Chip label="Off" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => handleEditClick(job.id)}
                        sx={{ mr: 1 }}
                      >
                        Edit
                      </Button>
                      {job.type === 'latest_news_agent' &&
                        countFoundLatestNews(job.run_summary) > 0 && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            sx={{ mr: 1 }}
                            onClick={() =>
                              openLatestNewsReview(
                                job.run_summary,
                                environment
                              )
                            }
                          >
                            Review found PRs
                          </Button>
                        )}
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => handleRunNowClick(job.id)}
                        disabled={runningJobId === job.id}
                      >
                        {runningJobId === job.id ? 'Running…' : 'Run Now'}
                      </Button>
                    </TableCell>
                  </TableRow>
                  {expandedJobId === job.id &&
                    (job.run_summary?.log_lines?.length ?? 0) > 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          sx={{
                            py: 1,
                            px: 2,
                            bgcolor: 'action.hover',
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography
                            variant="caption"
                            component="pre"
                            sx={{
                              fontFamily: 'monospace',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              fontSize: '0.75rem',
                            }}
                          >
                            {(job.run_summary?.log_lines ?? []).join('\n')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <RunOptionsDialog
        open={runOptionsDialogOpen}
        jobDisplayName={runOptionsJob?.display_name || ''}
        jobType={runOptionsJob?.type}
        defaultEnvironment={environment}
        defaultEmailEnabled={runOptionsJob?.email_enabled ?? false}
        onClose={() => {
          setRunOptionsDialogOpen(false);
          setRunOptionsJob(null);
        }}
        onRun={handleRunWithOptions}
      />

      <EditJobDialog
        schedulerTimezone={schedulerTimezone}
        open={editDialogOpen}
        job={editJob}
        environment={environment}
        onClose={() => {
          setEditDialogOpen(false);
          setEditJob(null);
        }}
        onSave={handleEditSave}
      />

      <RunResultDialog
        open={runResultDialogOpen}
        onClose={() => {
          setRunResultDialogOpen(false);
          setRunResult(null);
          setRunResultJobType(undefined);
          setRunResultDialogRunning(false);
        }}
        result={runResult}
        jobDisplayName={runResultJobName}
        jobType={runResultJobType}
        isRunning={runResultDialogRunning}
        onCancel={handleCancelRun}
        onReviewLatestNews={() =>
          openLatestNewsReview(
            runResult?.run_summary,
            runResult?.environment_used
          )
        }
      />

      <BatchBackfillReviewDialog
        open={batchReviewOpen}
        onClose={() => setBatchReviewOpen(false)}
        items={batchReviewItems}
        daysBack={batchDaysBack}
        feedType={batchFeedType}
        onCompanyIngested={() => {}}
        onComplete={() => {
          // Called after each successful company save; keep dialog open for more.
          showNotification('Press releases saved to Neo4j', 'success');
        }}
      />

      {jobs.some((j) => j.last_run_status === 'failure' && j.last_run_error) && (
        <Box mt={2}>
          <Typography variant="subtitle2" gutterBottom>
            Last run errors
          </Typography>
          {jobs
            .filter((j) => j.last_run_status === 'failure' && j.last_run_error)
            .map((job) => (
              <Paper key={job.id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {job.display_name}
                </Typography>
                <Typography
                  variant="body2"
                  color="error"
                  component="pre"
                  sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.5 }}
                >
                  {job.last_run_error}
                </Typography>
              </Paper>
            ))}
        </Box>
      )}
    </Container>
  );
};

export default ScheduledJobsPage;
